import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "@/lib/bizday";
import { CalendarHeart, Clock, Moon } from "lucide-react";
import api from "@/lib/api";
import { Title, Eyebrow, Nav, Chip } from "./ui";

const days = (n) => Array.from({ length: n }, (_, i) => dayjs().add(i, "day"));
const PARTS = [["10:00", "Morning"], ["13:30", "Afternoon"], ["17:00", "Evening"]];

function DateGrid({ value, onPick, range, testid, n = 14 }) {
  return (
    <div className="grid grid-cols-4 gap-3 sm:grid-cols-7" data-testid={testid}>
      {days(n).map((d) => {
        const k = d.format("YYYY-MM-DD");
        const sel = value === k || (range && (k === range[0] || k === range[1]));
        const inside = range && range[0] && range[1] && k > range[0] && k < range[1];
        return (
          <button type="button" key={k} onClick={() => onPick(k)} aria-pressed={sel} data-testid={`${testid}-${k}`}
            className={`flex min-h-[88px] flex-col items-center justify-center rounded-3xl transition-[background-color,box-shadow,color] duration-200 ${sel ? "bg-[var(--sb-plum)] text-white shadow-[0_12px_28px_rgba(74,21,75,.3)]" : inside ? "bg-[var(--sb-lav-bg)] text-[var(--sb-plum)]" : "bg-white ring-1 ring-[var(--sb-line)] hover:ring-[var(--sb-lav)]"}`}>
            <span className={`text-xs font-semibold uppercase tracking-widest ${sel ? "text-white/80" : "text-[var(--sb-muted)]"}`}>{d.isSame(dayjs(), "day") ? "Today" : d.format("ddd")}</span>
            <span className="sb-serif text-3xl font-semibold leading-none">{d.format("D")}</span>
            <span className={`text-xs ${sel ? "text-white/80" : "text-[var(--sb-muted)]"}`}>{d.format("MMM")}</span>
          </button>
        );
      })}
    </div>
  );
}

export function When({ f, set, needDay, needStay, needVet, reasons, onNext, onBack }) {
  const t = useQuery({ queryKey: ["sb-times", f.date], enabled: needDay && !!f.date,
    queryFn: async () => (await api.get("/self-booking/times", { params: { date: f.date } })).data });
  const slots = t.data?.slots || [];
  const nights = f.check_in && f.check_out ? dayjs(f.check_out).diff(dayjs(f.check_in), "day") : 0;
  const pickStay = (k) => {
    if (!f.check_in || (f.check_in && f.check_out) || k <= f.check_in) set({ check_in: k, check_out: "" });
    else set({ check_out: k });
  };
  const toggleReason = (id) => set({ reasons: f.reasons.some((r) => r.id === id) ? f.reasons.filter((r) => r.id !== id) : [...f.reasons, { id, note: "" }] });
  const other = f.reasons.find((r) => r.id === "other");
  const timeOk = !needDay || (f.date && f.time);
  const stayOk = !needStay || nights >= 1;
  const vetOk = !needVet || (f.reasons.length && (!other || other.note.trim()));
  const noTimes = t.isSuccess && t.data.configured && !slots.length;
  const timeOptions = useMemo(() => {
    const opts = t.data?.configured ? slots.map((s) => [s, dayjs(`2000-01-01T${s}`).format("h:mm A")]) : PARTS;
    const ist = new Date(Date.now() + 330 * 60000).toISOString();
    return f.date === ist.slice(0, 10) ? opts.filter(([v]) => v > ist.slice(11, 16)) : opts;
  }, [t.data, slots, f.date]);
  return (
    <div data-testid="sb-step-when">
      <Eyebrow>Timing</Eyebrow>
      <div className="mt-3"><Title sub="Pick what suits you best — our team will confirm it with you.">When would you like to visit?</Title></div>
      {needDay && (
        <section className="mt-10">
          <p className="mb-4 flex items-center gap-2 text-lg font-semibold"><CalendarHeart className="h-5 w-5 text-[var(--sb-lav)]" /> Choose a day</p>
          <DateGrid value={f.date} onPick={(k) => set({ date: k, time: "" })} testid="sb-date" />
          {f.date && (
            <div className="mt-8" data-testid="sb-times">
              <p className="mb-4 flex items-center gap-2 text-lg font-semibold"><Clock className="h-5 w-5 text-[var(--sb-lav)]" /> {t.data?.configured ? "Preferred time" : "Preferred time of day"}</p>
              {t.isLoading && <div className="h-14 w-64 animate-pulse rounded-full bg-[var(--sb-cream)]" />}
              {noTimes && <p className="text-[var(--sb-muted)]" data-testid="sb-no-times">We&rsquo;re closed or fully past opening hours on this day. Please choose another day.</p>}
              {t.isSuccess && !noTimes && <div className="flex flex-wrap gap-3">{timeOptions.map(([v, l]) => <Chip key={v} selected={f.time === v} onClick={() => set({ time: v })} testid={`sb-time-${v.replace(":", "")}`}>{l}</Chip>)}</div>}
              {t.isSuccess && !t.data.configured && <p className="mt-3 text-sm text-[var(--sb-muted)]">Opening hours aren&rsquo;t set up here, so reception will confirm the exact time.</p>}
              {t.isError && <p className="text-[#B4232A]" role="alert">We couldn&rsquo;t load times. Please try again.</p>}
            </div>
          )}
        </section>
      )}
      {needStay && (
        <section className="mt-12" data-testid="sb-stay">
          <p className="mb-1 flex items-center gap-2 text-lg font-semibold"><Moon className="h-5 w-5 text-[var(--sb-lav)]" /> Boarding stay</p>
          <p className="mb-4 text-sm text-[var(--sb-muted)]">{!f.check_in ? "Tap your check-in day." : !f.check_out ? "Now tap your check-out day." : `${nights} night${nights > 1 ? "s" : ""} · ${dayjs(f.check_in).format("D MMM")} to ${dayjs(f.check_out).format("D MMM")}`}</p>
          <DateGrid range={[f.check_in, f.check_out]} onPick={pickStay} testid="sb-stay-date" n={28} />
        </section>
      )}
      {needVet && (
        <section className="mt-12" data-testid="sb-reasons">
          <p className="mb-4 text-lg font-semibold">What brings you to the vet?</p>
          <div className="flex flex-wrap gap-3">{(reasons || []).map((r) => <Chip key={r.id} selected={f.reasons.some((x) => x.id === r.id)} onClick={() => toggleReason(r.id)} testid={`sb-reason-${r.id}`}>{r.label}</Chip>)}</div>
          {other && <input value={other.note} maxLength={120} onChange={(e) => set({ reasons: f.reasons.map((r) => (r.id === "other" ? { ...r, note: e.target.value } : r)) })} placeholder="Tell us a little more" className="sb-input mt-4 max-w-xl" enterKeyHint="done" data-testid="sb-reason-other-note" />}
        </section>
      )}
      <Nav onBack={onBack} onNext={onNext} disabled={!(timeOk && stayOk && vetOk)} nextLabel="Review my request" />
    </div>
  );
}
